import React from 'react';
import { useRouter } from 'next/router';
import { useQuery, gql } from '@apollo/client';

import withApollo from 'data/with-apollo';
import NotFoundPage from 'pages/404';
import PageLayout from 'components/page-layout';
import TornSection from 'components/torn-section';
import PageContainer from 'components/page-container';
import PageTitle from 'components/page-title';
import Tiles from 'components/tiles';
import Image from 'components/image';
import HTML from 'components/html';
import SocialLink from 'components/social-link';

const GET_ARTIST = gql`
  query GetArtist($id: ID!) {
    portfolioEntry(id: $id, idType: SLUG) {
      sites: termSlugs(taxonomies: [SITE])
      slug

      title
      content(format: RENDERED)

      featuredImage {
        sourceUrl
        altText
      }

      links {
        twitterUrl
        facebookUrl
        instagramUrl
        spotifyUrl
        websiteUrl
        tourUrl
      }

      media {
        featuredRelease {
          oembed {
            html
          }
        }
      }
    }
  }
`;

const ArtistPage = () => {
  const router = useRouter();
  const { data, loading } = useQuery(GET_ARTIST, { variables: { id: router.query.slug } });
  const artist = data?.portfolioEntry;

  if (!artist && loading) return null;
  if (!artist || !artist.sites.includes('bhr')) return <NotFoundPage />;

  const links = artist.links ?? {};
  const release = artist.media.featuredRelease;

  return (
    <PageLayout>
      <TornSection as="main" className="my-12 lg:my-24">
        <PageContainer>
          <Tiles container="-ml-16 -mt-12" tile="pl-16 pt-12 lg:w-1/2">
            <div>
              <Image
                src={artist.featuredImage.sourceUrl}
                alt={artist.featuredImage.altText || artist.title}
                className="block max-w-full mx-auto"
                sizes="100vw,50vw"
                auto={['compression', 'format']}
              />
              {release?.oembed?.html && (
                <div className="mt-12">
                  <h2 className="font-display leading-tight uppercase text-2xl font-bold text-white mb-6">
                    Latest Release
                  </h2>
                  <div className="shadow-xl">
                    <HTML>{release.oembed.html}</HTML>
                  </div>
                </div>
              )}
            </div>
            <div>
              <PageTitle>{artist.title}</PageTitle>
              <HTML className="text-gray-base text-lg leading-loose text-shadow rich-text -mt-4">
                {artist.content}
              </HTML>
              {Object.keys(links).length > 0 && (
                <Tiles as="section" container="-ml-4 pt-8" tile="pl-4">
                  {links.instagramUrl && (
                    <SocialLink service="instagram" href={links.instagramUrl} size="sm" />
                  )}
                  {links.youtubeUrl && (
                    <SocialLink service="youtube" href={links.youtubeUrl} size="sm" />
                  )}
                  {links.facebookUrl && (
                    <SocialLink service="facebook" href={links.facebookUrl} size="sm" />
                  )}
                  {links.twitterUrl && (
                    <SocialLink service="twitter" href={links.twitterUrl} size="sm" />
                  )}
                  {links.spotifyUrl && (
                    <SocialLink service="spotify" href={links.spotifyUrl} size="sm" />
                  )}
                  {links.websiteUrl && (
                    <SocialLink service="web" href={links.websiteUrl} size="sm" />
                  )}
                  {links.tourUrl && <SocialLink service="tour" href={links.tourUrl} size="sm" />}
                </Tiles>
              )}
            </div>
          </Tiles>
        </PageContainer>
      </TornSection>
    </PageLayout>
  );
};

export default withApollo(ArtistPage);
